import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Pagination from '../../components/ui/Pagination';
import { SessionListSkeleton } from '../../components/ui/LoadingSkeleton';
import { notifications } from '../../utils/toast';
import { participantService } from '../../services';
import { formatLocalDate } from '../../utils/dateUtils';

/**
 * Certificates List Page
 * Shows all earned certificates for the participant
 */

const CertificatesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [filter, setFilter] = useState('all'); // all, recent
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCertificates, setTotalCertificates] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchCertificates();
  }, [currentPage]);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const response = await participantService.getCertificates({
        page: currentPage,
        limit: itemsPerPage,
      });

      if (response.success) {
        const certs = (response.data || []).map((cert) => ({
          id: cert.id,
          // Previously sessionId mirrored cert.id, which made every cert
          // navigate with the cert's own _id as a sessionId — guaranteed 404
          // or load the wrong session. Use the explicit sessionId from the
          // API; cert.id fallback only kicks in if the API hasn't been
          // updated to return it yet.
          sessionId: cert.sessionId || cert.session?._id || cert.id,
          sessionName: cert.sessionName,
          organizationName: cert.organizationName,
          organizationLogo: null,
          trainerName: cert.trainerName,
          completionDate: cert.completedDate,
          issueDate: cert.completedDate,
          duration: cert.duration ? `${Math.round(cert.duration / 60)} hours` : '2 hours',
          certificateNumber: `CERT-${new Date(cert.completedDate).getFullYear()}-${cert.id?.slice(-6) || '000000'}`,
          skills: [],
        }));
        setCertificates(certs);
        setTotalPages(response.pagination?.pages || 1);
        setTotalCertificates(response.total || certs.length);
      }
    } catch (error) {
      console.error('Error fetching certificates:', error);
      notifications.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCertificate = (certificate) => {
    navigate(`/participant/certificates/${certificate.id}`);
  };

  const handleDownloadCertificate = (certificate, e) => {
    e.stopPropagation();
    // TODO: Implement PDF generation and download
    notifications.success(`Certificate "${certificate.sessionName}" downloaded!`);
  };

  const handleShareCertificate = (certificate, e) => {
    e.stopPropagation();
    // TODO: Implement share functionality
    notifications.info('Share functionality coming soon!');
  };

  const filteredCertificates = certificates.filter((cert) => {
    if (filter === 'all') return true;
    if (filter === 'recent') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return new Date(cert.issueDate) > thirtyDaysAgo;
    }
    return true;
  });

  return (
    <DashboardLayout title="My Certificates">
      <div className="p-6 space-y-6">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/participant/dashboard')}
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors mb-4 group"
          >
            <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Dashboard</span>
          </button>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">My Certificates</h1>
            <p className="text-neutral-600 mt-1">
              View and download your training certificates
            </p>
          </div>
          <Badge variant="success" size="lg">
            {totalCertificates} Earned
          </Badge>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 border-b border-neutral-200">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 font-medium transition-colors ${
              filter === 'all'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            All Certificates ({totalCertificates})
          </button>
          <button
            onClick={() => setFilter('recent')}
            className={`px-4 py-2 font-medium transition-colors ${
              filter === 'recent'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Recent (Last 30 Days)
          </button>
        </div>

        {/* Certificates Grid */}
        {loading ? (
          <SessionListSkeleton count={3} />
        ) : filteredCertificates.length === 0 ? (
          <Card padding="xl">
            <div className="text-center py-12">
              <svg
                className="w-20 h-20 mx-auto text-neutral-300 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Certificates Yet</h3>
              <p className="text-neutral-600 mb-6">
                Complete training sessions to earn certificates
              </p>
              <Button variant="primary" onClick={() => navigate('/join')}>
                Join a Session
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredCertificates.map((certificate) => (
              <motion.div
                key={certificate.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card
                  padding="none"
                  hoverable
                  className="cursor-pointer overflow-hidden"
                  onClick={() => handleViewCertificate(certificate)}
                >
                  {/* Certificate Preview */}
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 border-b-4 border-amber-400">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-400 rounded-full mb-4">
                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-neutral-900 mb-2">
                        Certificate of Completion
                      </h3>
                      <p className="text-lg font-semibold text-neutral-800">
                        {certificate.sessionName}
                      </p>
                      <p className="text-sm text-neutral-600 mt-2">
                        {certificate.organizationName}
                      </p>
                    </div>
                  </div>

                  {/* Certificate Details */}
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-neutral-500">Issue Date</p>
                        <p className="font-medium text-neutral-900">
                          {formatLocalDate(certificate.issueDate)}
                        </p>
                      </div>
                      <div>
                        <p className="text-neutral-500">Score</p>
                        <p className="font-medium text-neutral-900">{certificate.score}%</p>
                      </div>
                      <div>
                        <p className="text-neutral-500">Duration</p>
                        <p className="font-medium text-neutral-900">{certificate.duration}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500">Trainer</p>
                        <p className="font-medium text-neutral-900">{certificate.trainerName}</p>
                      </div>
                    </div>

                    {/* Skills */}
                    <div>
                      <p className="text-xs text-neutral-500 mb-2">Skills Acquired</p>
                      <div className="flex flex-wrap gap-2">
                        {certificate.skills.map((skill, index) => (
                          <Badge key={index} variant="outline" size="sm">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Certificate Number */}
                    <div className="pt-3 border-t border-neutral-200">
                      <p className="text-xs text-neutral-500">Certificate Number</p>
                      <p className="font-mono text-xs text-neutral-700 mt-1">
                        {certificate.certificateNumber}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="primary"
                        size="sm"
                        fullWidth
                        onClick={() => handleViewCertificate(certificate)}
                      >
                        View Certificate
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleDownloadCertificate(certificate, e)}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleShareCertificate(certificate, e)}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                          />
                        </svg>
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalCertificates > itemsPerPage && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalCertificates}
            />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CertificatesPage;
